/**
 * Side-by-side panel header for the compare-sales table. Renders the
 * source label + reference, status chip with optional "Expected: …"
 * warning, client + date subtitle. Used twice per compare view (LS
 * left, Xero right) and exported here so future alignment surfaces
 * (Skimmer / Pooltrackr ↔ Xero) can reuse the same component.
 *
 * Status chip props delegate to `saleStatusDisplay.statusDisplay` so
 * this component shares vocabulary with the inspector exception table.
 */
import React from 'react'
import { Box, Chip, Link, Stack, Tooltip, Typography } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import {
  handleLightspeedLinkClick,
  handleXeroLinkClick,
} from '../../../utils/externalLinks'
import { formatBusinessDate } from '../../../utils/inspectorFormatters'
import {
  SaleStatusDisplay,
  statusDisplay,
} from '../../../utils/saleStatusDisplay'
import { COMPARE_COLORS } from './compareSalesPalette'

export interface SystemPanelHeaderProps {
  icon: React.ReactNode
  systemLabel: string
  system: 'lightspeed' | 'xero'
  reference?: string
  link?: string
  client?: string | null
  date?: string | null
  status?: string | null
  /**
   * Canonical status this system should be in, given the other side's
   * state. When set and it differs from `status`, a small ⚠ icon
   * renders to the LEFT of the chip with an "Expected: …" tooltip.
   * Mirrors the table cell's status warning so analysts get the same
   * cue in both views.
   */
  expectedStatus?: SaleStatusDisplay | null
  clientMismatch?: boolean
  dateMismatch?: boolean
}

const SystemPanelHeader: React.FC<SystemPanelHeaderProps> = ({
  icon,
  systemLabel,
  system,
  reference,
  link,
  client,
  date,
  status,
  expectedStatus,
  clientMismatch,
  dateMismatch,
}) => {
  const statusChip = statusDisplay(system, status)
  const showStatusWarning =
    !!expectedStatus && (!statusChip || statusChip.label !== expectedStatus.label)
  const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!link) return
    const itemName = `${systemLabel} ${reference || ''}`.trim()
    if (system === 'lightspeed') {
      handleLightspeedLinkClick(e, link, itemName)
    } else {
      handleXeroLinkClick(e, link, itemName)
    }
  }

  const titleContent = (
    <Stack direction="row" alignItems="center" sx={{ gap: '12px' }}>
      <Typography
        sx={{
          fontSize: '1.05rem',
          fontWeight: 700,
        }}
      >
        {systemLabel}
      </Typography>
      {reference && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: '0.9rem',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            {reference}
          </Typography>
          {link && <OpenInNewIcon sx={{ fontSize: 15, color: 'text.secondary' }} />}
        </Stack>
      )}
    </Stack>
  )

  const titleBlock = link ? (
    <Link
      href={link}
      onClick={onClick}
      underline="hover"
      sx={{
        display: 'inline-flex',
        color: 'text.primary',
        textDecorationColor: 'rgba(0,0,0,0.2)',
      }}
    >
      {titleContent}
    </Link>
  ) : (
    titleContent
  )

  const metaIconSx = { fontSize: 14, color: 'text.disabled' } as const
  const metaTextSx = {
    fontSize: '0.78rem',
    color: 'text.secondary',
    lineHeight: 1.2,
  } as const

  // Two-zone header: left has icon + system name + reference with the
  // customer metadata directly underneath the title. Right has the
  // status pill stacked over the sale date. Outer Stack alignItems:
  // flex-start keeps the icon + title aligned to the top so LS and
  // Xero headers share identical height regardless of which side has
  // the longer customer name.
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={2}
      sx={{ minHeight: 56 }}
    >
      {/* LEFT: icon + (title block over customer row) */}
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="flex-start"
        // Drops the entire left zone (icon + title + customer) 3px so
        // it sits visually balanced against the right zone above.
        // ``pt`` is reliable here - mt on a flex item can be ignored
        // by some alignItems configurations.
        sx={{ minWidth: 0, flex: 1, pt: '3px' }}
      >
        <Box sx={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', mt: 0.25 }}>
          {icon}
        </Box>
        <Stack
          alignItems="flex-start"
          // Drops the whole title + customer block 1px so it sits
          // visually centred against the icon and status pill.
          sx={{ minWidth: 0, pt: '1px' }}
        >
          {titleBlock}
          {client !== undefined && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              // Customer row offset relative to the title above. ``-1px``
              // gives a tight visual stack without overlapping while
              // still pulling the customer up from MUI Stack's default
              // spacing.
              sx={{ minWidth: 0, mt: '-1px' }}
            >
              <PersonOutlineIcon
                sx={{
                  ...metaIconSx,
                  color: clientMismatch ? COMPARE_COLORS.mismatchBorder : metaIconSx.color,
                }}
              />
              <Tooltip title={client || ''} arrow placement="top" disableHoverListener={!client}>
                <Typography
                  sx={{
                    fontSize: '0.78rem',
                    fontWeight: clientMismatch ? 600 : 400,
                    color: clientMismatch
                      ? COMPARE_COLORS.mismatchBorder
                      : 'text.secondary',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                    maxWidth: '32ch',
                  }}
                >
                  {client || '-'}
                </Typography>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </Stack>

      {/* RIGHT: status pill stacked over sale date */}
      <Stack
        alignItems="flex-end"
        spacing={0.5}
        sx={{
          flexShrink: 0,
          // Lift the whole status + date cluster 2px so it sits
          // tighter against the top of the header.
          mt: '-2px',
        }}
      >
        {statusChip ? (
          (() => {
            const chip = (
              <Chip
                label={
                  showStatusWarning ? (
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {statusChip.label}
                      <WarningAmberIcon sx={{ color: '#ED6C02', fontSize: 16 }} />
                    </Box>
                  ) : (
                    statusChip.label
                  )
                }
                color={statusChip.color === 'default' ? 'default' : statusChip.color}
                size="small"
                variant="outlined"
                sx={{
                  height: 28,
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  borderWidth: 1.5,
                  backgroundColor: '#FFFFFF',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )
            return showStatusWarning && expectedStatus ? (
              <Tooltip title={`Expected: ${expectedStatus.label}`} arrow placement="top">
                <Box component="span" sx={{ display: 'inline-flex', cursor: 'help' }}>
                  {chip}
                </Box>
              </Tooltip>
            ) : (
              chip
            )
          })()
        ) : (
          <Box sx={{ height: 28 }} />
        )}
        {date !== undefined && (
          <Stack
            direction="row"
            spacing={0.4}
            alignItems="center"
            sx={{ mt: 0.25 }}
          >
            <EventOutlinedIcon
              sx={{
                fontSize: 14,
                color: dateMismatch
                  ? COMPARE_COLORS.mismatchBorder
                  : 'text.secondary',
              }}
            />
            <Typography
              sx={{
                fontSize: '0.78rem',
                // Muted grey by default - reads as secondary metadata
                // under the status pill. Only switches to the mismatch
                // tone when the two sides' dates diverge.
                color: dateMismatch
                  ? COMPARE_COLORS.mismatchBorder
                  : 'text.secondary',
                fontWeight: dateMismatch ? 600 : 400,
                whiteSpace: 'nowrap',
              }}
            >
              {date ? formatBusinessDate(date) : '-'}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

export default SystemPanelHeader
